import { Slider } from "../ui/slider";
import { useDeck } from "../../lib/deck-context";
import { fmtM, fmtUsd } from "../../lib/model";
import { Card, Eyebrow, Note, Reveal, Section, SectionHeader, StatusBadge } from "../deck/primitives";
import { Waterfall } from "../deck/Waterfall";

const CONTROLS = [
  { k: "rent", label: "Weighted avg rent", min: 24, max: 48, step: 0.5, fmt: (v) => `$${v.toFixed(2)} / SF` },
  { k: "occ", label: "Stabilized occupancy", min: 75, max: 100, step: 0.5, fmt: (v) => `${v}%` },
  { k: "other", label: "Other revenue", min: 0, max: 300000, step: 5000, fmt: (v) => fmtUsd(v) },
  { k: "exp", label: "Expense / reserve load", min: 5, max: 30, step: 0.5, fmt: (v) => `${v}%` },
];

const Ledger = ({ m, a }) => {
  const rows = [
    ["Scheduled Base Rent", `${a.sf.toLocaleString()} SF × $${a.rent}/SF`, fmtM(m.scheduled, 3)],
    ["Stabilized Occupancy", `× ${a.occ}%`, fmtM(m.occupied, 3)],
    ["Other Revenue", `+ ${fmtUsd(a.other)}`, fmtM(m.effective, 3)],
    ["Nonrecoverable Expenses / Reserves", `− ${a.exp}%`, fmtM(m.noiPre, 3)],
  ];
  if (a.groundRent > 0) rows.push(["City Ground Rent", `− ${fmtUsd(a.groundRent)}`, fmtM(m.noi, 3)]);
  return (
    <Card className="p-7 h-full flex flex-col">
      <Eyebrow>The Exact Calculation</Eyebrow>
      <div className="mt-5 flex-1">
        {rows.map(([t, op, v], i) => (
          <div key={t} className="py-3.5 border-b border-line/70">
            <div className="text-xs text-steel">{t}</div>
            <div className="mt-1 flex items-baseline justify-between gap-3">
              <span className="font-mono text-sm text-char">{op}</span>
              <span data-testid={`ledger-value-${i}`} className="tabular-nums text-ink">= {v}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 rounded-xl bg-arq text-white px-6 py-5">
        <div className="font-mono text-[10px] tracking-[0.2em] text-white/70">NET OPERATING INCOME</div>
        <div data-testid="revenue-noi-value" className="mt-2 font-display text-4xl tabular-nums">{fmtM(m.noi, 3)}</div>
      </div>
    </Card>
  );
};

export default function Revenue() {
  const { m, inputs, setInput } = useDeck();
  const steps = [
    { label: "Scheduled base rent", value: m.scheduled, kind: "total" },
    { label: `Vacancy (${(100 - inputs.occ).toFixed(1)}%)`, value: m.vacancy, kind: "sub" },
    { label: "Other revenue", value: inputs.other, kind: "add" },
    { label: "Effective revenue", value: m.effective, kind: "total" },
    { label: `Expenses / reserves (${inputs.exp}%)`, value: m.expenses, kind: "sub" },
    ...(inputs.groundRent > 0 ? [{ label: "City ground rent", value: inputs.groundRent, kind: "sub" }] : []),
    { label: "Net Operating Income", value: m.noi, kind: "total" },
  ];
  return (
    <Section testid="section-revenue">
      <SectionHeader num="05" eyebrow="Operating Model · Revenue + NOI" title="How the project makes money." lede="A transparent build from scheduled rent to stabilized Net Operating Income. Adjust any driver and the full underwriting model recalculates." status="preliminary" />
      <div className="grid xl:grid-cols-12 gap-6">
        <Reveal className="xl:col-span-8">
          <Card className="p-7 lg:p-9">
            <div className="flex items-center justify-between mb-8"><Eyebrow>Revenue Waterfall</Eyebrow><span className="text-xs text-steel">Annual · stabilized</span></div>
            <Waterfall steps={steps} testid="revenue-waterfall-chart" height={320} />
            <div className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-7">
              {CONTROLS.map((c) => (
                <div key={c.k}>
                  <div className="flex justify-between text-sm mb-3">
                    <span className="text-steel">{c.label}</span>
                    <span data-testid={`revenue-control-value-${c.k}`} className="tabular-nums text-ink font-medium">{c.fmt(inputs[c.k])}</span>
                  </div>
                  <Slider data-testid={`revenue-slider-${c.k}`} min={c.min} max={c.max} step={c.step} value={[inputs[c.k]]} onValueChange={([v]) => setInput(c.k, v)} />
                </div>
              ))}
            </div>
          </Card>
        </Reveal>
        <Reveal delay={0.1} className="xl:col-span-4"><Ledger m={m} a={inputs} /></Reveal>
      </div>
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <Note testid="revenue-rent-explainer" tone="accent">
          The <strong>$36/SF weighted average rent</strong> is a premium underwriting target for new small-format restaurant, food-hall, vendor and experiential commercial space. It is not intended to represent average Murrieta retail rent.
        </Note>
        <Note testid="revenue-broker-validation">
          <div className="flex items-center gap-3 mb-1"><StatusBadge s="tbv" /><strong className="text-ink">Broker validation required before final investment decision.</strong></div>
          If broker-validated rent comes down, valuation and acceptable project budget come down with it. NOI shown before final City ground rent unless entered in Assumptions.
        </Note>
      </div>
    </Section>
  );
}
